<script setup lang="ts">
    import { computed, inject, onMounted, reactive, ref, toRaw } from 'vue';
    import ConfirmationModal from '@/components/ConfirmationModal.vue';
    import ToastNotification from '@/components/ToastNotification.vue';
    import axios from 'axios';
    import { formatDate } from '../../utils';
    import { useRoute } from 'vue-router';
    import ImportMembresComponent from '../collectivites/collectivites/@components/ImportMembresComponent.vue';

    const BACKEND_URL = inject('BACKEND_URL');
    const route = useRoute();
    const refreshKey = ref(0);
    const errorMessage = ref('');
    const loading = ref(false);
    const loadingExportToExcel = ref(false);
    const loadingExportToPdf = ref(false);
    const loadingInvitations = ref(false);
    const toastMessage = ref('');
    const toastType = ref<'success' | 'error' | 'info' | 'warning'>('success');
    const showToast = ref(false);
    const itemsList = ref([] as any[]);
    const selectedItem = ref(null as any);
    const confirmationModalRef = ref<InstanceType<typeof ConfirmationModal> | null>(null);
    const selectedItems = ref([] as any[]);
    const areAllItemsSelected = computed(() => {
        return itemsList.value.length == selectedItems.value.length;
    });
    const startupInfos = reactive({
        collectivites: [] as any[],
        pays: [] as any[]
    });
    const queryParams = reactive({
        pageNumber: 1,
        sortDir: 'ASC',
        sortColumn: 'fullname',
        sortOption: 'fullname_asc',
        borneInf: '',
        borneSup: '',
        pays_id: '',
        collectivite_id: '',
        search: '',
        trashed: 0
    });
    const pagination = reactive({
        total: 0,
        per_page: 10,
        last_page: 1
    });
    const SORT_OPTIONS = [
        { value: 'fullname_asc', label: 'Nom & Prénoms (ASC)', sort_column: 'fullname', sort_direction: 'ASC' },
        { value: 'fullname_desc', label: 'Nom & Prénoms (DESC)', sort_column: 'fullname', sort_direction: 'DESC' },
        { value: 'created_at_asc', label: 'Date (ASC)', sort_column: 'created_at', sort_direction: 'ASC' },
        { value: 'created_at_desc', label: 'Date (DESC)', sort_column: 'created_at', sort_direction: 'DESC' },
    ]
    const showAdvancedFilters = ref(false);

    onMounted(() => {
        const startupDatas: any = route.meta.startupinfos_datas;
        Object.assign(startupInfos, startupDatas);

        // Lire la variable sort_dir dans le query params de l'URL
        const sortParam = route.query.sort as string;
        if (sortParam) { queryParams.sortOption = sortParam; onFiltersChanged(); }
        
        // Lire collectivite_id depuis les query params
        const collectiviteIdParam = route.query.collectivite_id as string;
        if (collectiviteIdParam) {
            queryParams.collectivite_id = collectiviteIdParam;
        }
        
        doListItems();
    });

    async function doListItems() {
        try {
            loading.value = true;
            errorMessage.value = '';
            const queryParamsRaw = toRaw(queryParams);
            const queryString = new URLSearchParams(Object.fromEntries(Object.entries(queryParamsRaw).map(([k,v]) => [k, String(v)]))).toString();
            const httpResponse = await axios.get(`/backoffice/users/list?${queryString}`);
            itemsList.value = httpResponse.data.data;
            pagination.total = httpResponse.data.total;
            pagination.per_page = httpResponse.data.per_page;
            pagination.last_page = httpResponse.data.last_page;
            selectedItems.value = [];
        } catch (error: any) {
            errorMessage.value = error.message;
        } finally {
            loading.value = false;
        }
    }

    async function doDeleteItem(item: any) {
        const confirmation = await confirmationModalRef.value?.open("Membres", "Voulez-vous vraiment supprimer l'élément sélectionné ?");
        if (confirmation) {
            try {
                loading.value = true;
                errorMessage.value = '';
                await axios.post(`/backoffice/users/destroy/${item.id}`);
                doListItems();
            } catch (error: any) {
                errorMessage.value = error.message;
            } finally {
                loading.value = false;
            }
        }
    }

    async function doRestoreItem(item: any) {
        const confirmation = await confirmationModalRef.value?.open("Membres", "Voulez-vous vraiment restaurer l'élément sélectionné ?");
        if (confirmation) {
            try {
                loading.value = true;
                errorMessage.value = '';
                await axios.post(`/backoffice/users/restore/${item.id}`);
                doListItems();
            } catch (error: any) {
                errorMessage.value = error.message;
            } finally {
                loading.value = false;
            }
        }
    }

    function doSelectItem(item: any) {
        selectedItem.value = item;
    }

    function doUnselectItem() {
        selectedItem.value = null;
    }

    async function doToggleEtatValideAction(itemsIds: any[]) {
        const confirmation = await confirmationModalRef.value?.open("Membres", "Voulez-vous vraiment modifier l'état des éléments sélectionnés ?");
        if (confirmation) {
            try {
                loading.value = true;
                errorMessage.value = '';
                await axios.post(`/backoffice/users/toggle_etat_valide`, { ids: itemsIds });

                itemsIds.forEach(itemId => {
                    const index = itemsList.value.findIndex(i => i.id == itemId);
                    if (index > -1) {
                        itemsList.value[index].actif = itemsList.value[index].actif == 1 ? 0 : 1;
                    }
                });

            } catch (error: any) {
                errorMessage.value = error.message;
            } finally {
                loading.value = false;
            }
        }
    }

    async function doExportToExcelAction() {
        try {
            loadingExportToExcel.value = true;
            errorMessage.value = '';
            const queryParamsRaw = toRaw(queryParams);
            const queryString = new URLSearchParams(Object.fromEntries(Object.entries(queryParamsRaw).map(([k,v]) => [k, String(v)]))).toString();
            const response = await axios.get(`/backoffice/users/export_excel?${queryString}`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'utilisateurs.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (error: any) {
            errorMessage.value = error.message;
        } finally {
            loadingExportToExcel.value = false;
        }
    }

    async function doExportToPdfAction() {
        try {
            loadingExportToPdf.value = true;
            errorMessage.value = '';
            const queryParamsRaw = toRaw(queryParams);
            const queryString = new URLSearchParams(Object.fromEntries(Object.entries(queryParamsRaw).map(([k,v]) => [k, String(v)]))).toString();
            const response = await axios.get(`/backoffice/users/export_pdf?${queryString}`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'utilisateurs.pdf');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (error: any) {
            errorMessage.value = error.message;
        } finally {
            loadingExportToPdf.value = false;
        }
    }

    function handleCheckAll(_event: any) {
        const checked = _event.target.checked;
        if (checked) {
            selectedItems.value = itemsList.value.map(i => i.id);
        } else {
            selectedItems.value = [];
        }
    }

    function onFiltersChanged() {
        const sortItem = SORT_OPTIONS.find(o => o.value == queryParams.sortOption);
        if (sortItem) {
            queryParams.sortColumn = sortItem.sort_column;
            queryParams.sortDir = sortItem.sort_direction;
        }
        doListItems();
    }

    function onPaginate(pageNumber: number) {
        queryParams.pageNumber = pageNumber;
        doListItems();
    }

    function toggleAdvancedFilters() {
        showAdvancedFilters.value = !showAdvancedFilters.value;
    }

    async function downloadCsv() {
        try {
            const response = await axios.get("/backoffice/collectivites/membres/download_csv_template", { responseType: "blob" });
            const blob = new Blob([response.data], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "collectivite_membres_template.csv");
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Erreur de téléchargement :", error);
        }
    }

    function onMembersImported() {
        refreshKey.value++;
        doListItems();
    }

    async function sendInvitationsUnverified() {
        if (!queryParams.collectivite_id && selectedItems.value.length === 0) return;
        
        try {
            // Récupérer le nombre de membres non vérifiés
            const countResponse = await axios.get('/backoffice/collectivites/membres/count_unverified', {
                params: { collectivite_id: queryParams.collectivite_id }
            });
            
            const unverifiedCount = countResponse.data.count || 0;
            
            if (unverifiedCount === 0) {
                toastMessage.value = "Aucun membre non vérifié trouvé pour cette collectivité";
                toastType.value = 'info';
                showToast.value = true;
                return;
            }
            
            // Afficher la modale de confirmation
            const confirmation = await confirmationModalRef.value?.open(
                "Envoyer les invitations",
                `Vous êtes sur le point d'envoyer ${unverifiedCount} invitation(s) aux membres non vérifiés de cette collectivité. Voulez-vous continuer ?`
            );
            
            if (!confirmation) {
                return;
            }
            
            loadingInvitations.value = true;
            toastMessage.value = '';
            showToast.value = false;
            
            const response = await axios.post('/backoffice/collectivites/membres/send_invitations_unverified', {
                collectivite_id: queryParams.collectivite_id
            });
            
            toastMessage.value = response.data.message || 'Invitations envoyées avec succès';
            toastType.value = 'success';
            showToast.value = true;
            
            // Rafraîchir la liste après l'envoi
            setTimeout(() => {
                doListItems();
            }, 1000);
        } catch (error: any) {
            console.error("Erreur lors de l'envoi des invitations:", error);
            toastMessage.value = error.response?.data?.message || "Une erreur est survenue lors de l'envoi des invitations";
            toastType.value = 'error';
            showToast.value = true;
        } finally {
            loadingInvitations.value = false;
        }
    }
</script>
<template>
    <section class="container-full-app bg-grey-50">
        <div class="page-space-30"></div>

        <!--*** Widget *** -->
        <section class="s20">
            <!-- Import CSV Component (affiché seulement si collectivite_id est présent) -->
            <div v-if="queryParams.collectivite_id" class="mb-5">
                <import-membres-component 
                    :collectivite-id="parseInt(queryParams.collectivite_id as string)" 
                    :key="refreshKey"
                    @completed="onMembersImported"
                />
            </div>
            
            <div class="widget shadow-100 s20 pb-2"><!-- widget shadow-100 fill -->
                <!--Card-->
                <div class="container-full-2 s20 my-5">
                    <div class="s7"><h4 class="font-semibold mt-4">Membres <span class="text-sm text-grey-500">(recent)</span></h4></div>
                    <div class="s13 flex justify-content-right">
                        <!-- Boutons d'import (affichés seulement si collectivite_id est présent) -->
                        <template v-if="queryParams.collectivite_id">
                            <button type="button" @click="downloadCsv" class="btn normal ml-3 justify-content-center btn-filtre">
                                <img src="/icons/icon-telecharger.svg" class="btn-icon" alt="" />
                                <span>Modèle CSV</span>
                            </button>
                            <label for="file-upload-membres-list" class="btn normal ml-3 justify-content-center btn-filtre" style="cursor: pointer;">
                                <img src="/icons/icon-upload.svg" class="btn-icon" alt="" />
                                <span>Importer CSV</span>
                            </label>
                            <button type="button" @click="sendInvitationsUnverified" class="btn normal ml-3 justify-content-center btn-filtre" :disabled="loadingInvitations || selectedItems.length == 0">
                                <img src="/icons/icon-send.svg" class="btn-icon" alt="" />
                                <span v-if="!loadingInvitations">Envoyer invitations ({{ selectedItems.length }})</span>
                                <span v-else>Envoi...</span>
                            </button>
                        </template>
                        <!-- <button class="btn normal ml-4 justify-content-center btn-filtre">
                            <img src="/icons/icon-add.svg" class="btn-icon" alt="" />
                            <span>Créer</span>
                        </button> -->
                        <div class="form-field-floating default sm s8_mobile ml-3">
                            <select class="form-input-select" id="filters_collectivite" v-model="queryParams.collectivite_id" @change="onFiltersChanged">
                                <option value="">Toutes les collectivités</option>
                                <option :value="option.id" v-for="(option, index) in startupInfos.collectivites" :key="'filter_collectivite_' + index">{{ option.nom }}</option>
                            </select>
                        </div>
                        <div class="form-field-floating default sm s4_mobile ml-3">
                            <select class="form-input-select" id="filters_sort" v-model="queryParams.sortOption" @change="onFiltersChanged">
                                <option :value="option.value" v-for="(option, index) in SORT_OPTIONS" :key="'filter_sort_' + index">{{ option.label }}</option>
                            </select>

                        </div>
                        <button @click="toggleAdvancedFilters" class="btn normal white2 s3_mobile ml-4 justify-content-center btn-filtre btn-filter">
                            <img src="/icons/icon-filter.svg" class="btn-icon" alt="" />
                            <span>Options</span>
                        </button>
                    </div>
                    <div class="clear-both"></div>
                    <!--Options-->
                    <div class="custom-filter-set-area" v-show="showAdvancedFilters">
                        <div class="grid-filter6 mt-4 mb-0">
                            <div class="form-field-floating reactive sm">
                                <input type="text" class="form-input" id="filters_search" v-model="queryParams.search" placeholder="" @keyup.enter="onFiltersChanged">
                                <label for="filters_search">Recherche</label>
                            </div>
                            <div class="form-field-floating reactive sm">
                                <input type="date" class="form-input" id="filters_borne_inf" v-model="queryParams.borneInf" placeholder="" @change="onFiltersChanged">
                                <label for="filters_borne_inf">Date de création >=</label>
                            </div>
                            <div class="form-field-floating reactive sm">
                                <input type="date" class="form-input" id="filters_borne_sup" v-model="queryParams.borneSup" placeholder="" @change="onFiltersChanged">
                                <label for="filters_borne_sup">Date de création &lt;=</label>
                            </div>
                            <div class="form-field-floating sm">
                                <select class="form-input-select" id="filters_pays" v-model="queryParams.pays_id" @change="onFiltersChanged">
                                    <option value="">Tous les pays</option>
                                    <option :value="option.id" v-for="(option, index) in startupInfos.pays" :key="'filter_pays_' + index">{{ option.nom }}</option>
                                </select>
                                <label for="filters_pays">Pays <b class="color-danger">*</b></label>
                            </div>
                            <div class="form-field-floating sm">
                                <select class="form-input-select" id="filters_trashed" v-model="queryParams.trashed" @change="onFiltersChanged">
                                    <option value="0">Membres valides</option>
                                    <option value="1">Membres en sommeil</option>
                                </select>
                                <label for="filters_trashed">Etat <b class="color-danger">*</b></label>
                            </div>
                            <custom-button classes-list="btn black normal mx-0 btn-filtre" :loading="loadingExportToExcel" @click="doExportToExcelAction">
                                <img src="/icons/icon-excel.svg" class="btn-icon" alt="" />
                                <span>Exporter Excel</span>
                            </custom-button>
                            <custom-button classes-list="btn black normal mx-0 btn-filtre" :loading="loadingExportToPdf" @click="doExportToPdfAction">
                                <img src="/icons/icon-pdf.svg" class="btn-icon" alt="" />
                                <span>Exporter PDF</span>
                            </custom-button>
                        </div>

                    </div>
                    <!---->
                </div>
            </div>

            <div>
                <table class="table separator skin3 selection rounded"><!-- selection skin1 skin2 (bg1 bg2 bg3)  (skin3 rounded)  sm-->
                    <thead>
                        <tr>
                            <th>
                                <label class="btn-circle" for="check-list1">
                                    <input type="checkbox" id="check_all" :checked="areAllItemsSelected" @change="handleCheckAll" />
                                </label>
                            </th>
                            <th></th>
                            <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Information</th>
                            <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Utilisation</th>
                            <th>Collectivité</th>
                            <th class="flex-right">
                                <button class="btn-ultra color2">
                                    <div class="btn-show">
                                        <img src="/icons/icon-actif.svg" class="btn-icon" alt="" />
                                        <span>Actif</span>
                                    </div>
                                    <div class="btn-loading">
                                        <div class="loader-dots"></div>
                                    </div>
                                </button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-if="itemsList.length == 0">
                            <tr>
                                <td colspan="6">
                                    <div class="text-center text-grey-700">Aucun élément trouvé</div>
                                </td>
                            </tr>
                        </template>

                        <tr class="text-grey-700 over-effect" v-for="(item, index) in itemsList" :key="'item_' + index"><!-- over-effect active-->
                            <td class="selection-item">
                                <label class="btn-circle" :for="'item_check_' + index">
                                    <input type="checkbox" name="selectedItems[]" :id="'item_check_' + index" :value="item.id" v-model="selectedItems" />
                                </label>
                            </td>
                            <td class="flex align-items-center bg-white h-100p">
                                <div class="square-xl rounded-15" style="background-color: rgb(var(--color-warning-rgb), 0.2);"><img :src="!!item.photo_url ? item.photo_url : '/assets/users_avatar/600w/user.jpg'" width="100%" /></div>
                                <div v-if="item.collectivite" class="square-xl rounded-15 ml-3 px-3 py-3" style="background-color: rgb(var(--color-black-rgb), 0.08);">
                                    <img src="/assets/logos/logo2.svg" width="100%" />
                                </div>
                            </td>
                            <td style="width: 20%;">
                                <p class="text-xl font-semibold mb-2">
                                    {{ item.nom + ' ' + item.prenom }} &nbsp; 
                                    <span v-if="!item.email_verified_at" class="tag-state warning stroke">En cours</span>
                                    <span v-if="item.email_verified_at" class="tag-state success stroke">Vérifié</span>
                                    <span v-if="item.actif == 1" class="tag-state notif stroke">Actif</span>
                                    <span v-if="item.actif == 0" class="tag-state danger stroke">Inactif</span>
                                </p>
                                <p class="text-sm text-grey-600">{{ item.email }}</p>
                                <p class="text-sm text-grey-600">{{ item.telephone }}</p>
                            </td>
                            <td class="text-3xl" style="width: 10%;">
                                <template v-if="item.quotite_cessible_pourcent">
                                    {{ item.quotite_cessible_pourcent }}%
                                </template>
                                <template v-else>-</template>
                                <p class="mt-1 text-sm">
                                    <template v-if="item.quotite_cessible_montant">
                                        {{ new Intl.NumberFormat('fr-FR').format(item.quotite_cessible_montant) }} F
                                    </template>
                                    <template v-else>-</template>
                                </p>
                            </td>
                            <td>
                                {{ item.collectivite?.nom || 'N/A' }}
                            </td>
                            <td>
                                <div class="flex-right gap-4">
                                    <router-link :to="'/app/users/form/' + item.id">
                                        <button class="btn tips">
                                            <img src="/icons/icon-edit.svg" class="btn-icon" alt="" />
                                            <span>Editer</span>
                                        </button>
                                    </router-link>
                                    <custom-button :classesList="'btn-ultra color2 tips'" @click="doToggleEtatValideAction([item.id])"  v-if="item.deleted_at == null && item.actif == 1">
                                        <img src="/icons/icon-actif.svg" class="btn-icon" alt="" />
                                        <span>Actif</span>
                                    </custom-button>
                                    <custom-button :classesList="'btn-ultra color2 tips'" @click="doToggleEtatValideAction([item.id])" v-if="item.deleted_at == null && item.actif == 0">
                                        <img src="/icons/icon-inactif.svg" class="btn-icon" alt="" />
                                        <span>Inactif</span>
                                    </custom-button>
                                    <router-link :to="'/app/users/' + item.id + '/details'">
                                        <button class="btn white2 active">
                                            <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                            <span>Gérer</span>
                                        </button>
                                    </router-link>
                                    <router-link :to="'/app/users/' + item.id + '/carte'">
                                        <button class="btn white2 active">
                                            <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                            <span>Carte</span>
                                        </button>
                                    </router-link>
                                    <router-link :to="'/app/users/' + item.id + '/utilisation'">
                                        <button class="btn white2 active">
                                            <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                            <span>Utilisation</span>
                                        </button>
                                    </router-link>
                                    <router-link :to="'/app/users/' + item.id + '/plan'">
                                        <button class="btn white2 active">
                                            <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                            <span>Plan</span>
                                        </button>
                                    </router-link>
                                    <router-link :to="'/app/users/' + item.id + '/revolving'">
                                        <button class="btn white2 active">
                                            <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                            <span>Revolving</span>
                                        </button>
                                    </router-link>
                                </div>
                            </td>
                        </tr>

                    </tbody>
                </table>
                <!--Pagination-->
                <div style="display: flex; flex-direction: row; justify-content: center;">
                    <custom-pagination v-model="queryParams.pageNumber" :total="pagination.total" :per-page="pagination.per_page" :last_page="pagination.last_page" @update:modelValue="onPaginate" />
                </div>
                <!---->
            </div>
            <div class="clear-both h-30"></div>
            
        </section>
        
        <div class="clear-both h-30"></div>
        <confirmation-modal ref="confirmationModalRef"></confirmation-modal>
        <toast-notification 
            :message="toastMessage" 
            :type="toastType" 
            :show="showToast" 
            @update:show="showToast = $event"
        />
    </section>
</template>
